import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface FormData {
  eventName: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  eventDescription: string;
  buildingSpaces: string[];
  technicalNeeds: string;
  expectedAttendees: string;
  foodBeverage: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  organizationType: string;
  organizationName: string;
  mailingAddress: string;
  cityStateZip: string;
}

const BUILDING_SPACES = [
  'Worship Center',
  'Catering Kitchen',
  'Fellowship Hall',
  'Preschool',
  'Lower Elementary',
  'Upper Elementary',
  'Multi-Purpose Gym',
  'Gathering Area',
  'Youth Room',
];

const FacilityUseForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    eventName: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    eventDescription: '',
    buildingSpaces: [],
    technicalNeeds: '',
    expectedAttendees: '',
    foodBeverage: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    organizationType: '',
    organizationName: '',
    mailingAddress: '',
    cityStateZip: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Set page title
  useEffect(() => {
    document.title = 'Facility Use Request - GraceHill Church';
    return () => {
      document.title = 'Church Planter';
    };
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (space: string) => {
    setFormData((prev) => ({
      ...prev,
      buildingSpaces: prev.buildingSpaces.includes(space)
        ? prev.buildingSpaces.filter((s) => s !== space)
        : [...prev.buildingSpaces, space],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // TODO: Replace with your Google Apps Script Web App URL
      const GOOGLE_SCRIPT_URL = import.meta.env.VITE_GOOGLE_SCRIPT_URL;
      
      if (!GOOGLE_SCRIPT_URL) {
        throw new Error('Form submission endpoint not configured. Please contact the administrator.');
      }

      // With no-cors mode, we can't read the response, so we assume success
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors', // Google Apps Script requires this
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          buildingSpaces: formData.buildingSpaces.join(', '),
          submittedAt: new Date().toISOString(),
        }),
      });
      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        eventName: '',
        eventDate: '',
        startTime: '',
        endTime: '',
        eventDescription: '',
        buildingSpaces: [],
        technicalNeeds: '',
        expectedAttendees: '',
        foodBeverage: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        organizationType: '',
        organizationName: '',
        mailingAddress: '',
        cityStateZip: '',
      });
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <FormContainer>
        <Header>
          <LogoSection>
            <ChurchName>GRACEHILL CHURCH</ChurchName>
          </LogoSection>
          <Title>Facility Use Request Form</Title>
          <Subtitle>Please complete all fields below to request facility use.</Subtitle>
        </Header>

        {submitStatus === 'success' && (
          <SuccessMessage>
            <h3>Thank you for your request!</h3>
            <p>Your facility use request has been submitted successfully. A member of our team will reach out to you soon.</p>
          </SuccessMessage>
        )}

        {submitStatus === 'error' && (
          <ErrorMessage>
            <h3>Submission Error</h3>
            <p>{errorMessage}</p>
          </ErrorMessage>
        )}

        {submitStatus !== 'success' && (
          <Form onSubmit={handleSubmit}>
            <Section>
              <SectionTitle>Event Information</SectionTitle>
              
              <FormGroup>
                <Label htmlFor="eventName">Name of Event *</Label>
                <Input
                  type="text"
                  id="eventName"
                  name="eventName"
                  value={formData.eventName}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="eventDate">Event Date *</Label>
                  <Input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="startTime">Start Time *</Label>
                  <Input
                    type="time"
                    id="startTime"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="endTime">End Time *</Label>
                  <Input
                    type="time"
                    id="endTime"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label htmlFor="eventDescription">Description of Event *</Label>
                <TextArea
                  id="eventDescription"
                  name="eventDescription"
                  value={formData.eventDescription}
                  onChange={handleInputChange}
                  rows={4}
                  required
                />
              </FormGroup>
            </Section>

            <Section>
              <SectionTitle>Building Space(s) Requested</SectionTitle>
              <CheckboxNote>Check all that apply</CheckboxNote>
              <CheckboxGrid>
                {BUILDING_SPACES.map((space) => (
                  <CheckboxLabel key={space}>
                    <Checkbox
                      type="checkbox"
                      checked={formData.buildingSpaces.includes(space)}
                      onChange={() => handleCheckboxChange(space)}
                    />
                    <CheckboxText>{space}</CheckboxText>
                  </CheckboxLabel>
                ))}
              </CheckboxGrid>
            </Section>

            <Section>
              <SectionTitle>Additional Details</SectionTitle>
              
              <FormGroup>
                <Label htmlFor="technicalNeeds">
                  Description of Technical Needs, Security Needs, and Set-Up Requirements
                </Label>
                <TextArea
                  id="technicalNeeds"
                  name="technicalNeeds"
                  value={formData.technicalNeeds}
                  onChange={handleInputChange}
                  rows={4}
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="expectedAttendees">Number of Attendees Expected *</Label>
                  <Input
                    type="number"
                    id="expectedAttendees"
                    name="expectedAttendees"
                    value={formData.expectedAttendees}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Will Food or Beverage Be Consumed? *</Label>
                  <RadioGroup>
                    <RadioLabel>
                      <Radio
                        type="radio"
                        name="foodBeverage"
                        value="Yes"
                        checked={formData.foodBeverage === 'Yes'}
                        onChange={handleInputChange}
                        required
                      />
                      Yes
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        type="radio"
                        name="foodBeverage"
                        value="No"
                        checked={formData.foodBeverage === 'No'}
                        onChange={handleInputChange}
                      />
                      No
                    </RadioLabel>
                  </RadioGroup>
                </FormGroup>
              </FormRow>
            </Section>

            <Section>
              <SectionTitle>Primary Point of Contact Information</SectionTitle>
              
              <FormGroup>
                <Label htmlFor="contactName">Name *</Label>
                <Input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="contactPhone">Phone *</Label>
                  <Input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="contactEmail">Email *</Label>
                  <Input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </FormRow>
            </Section>

            <Section>
              <SectionTitle>Organization Information</SectionTitle>
              
              <FormGroup>
                <Label>Organization Type</Label>
                <RadioGroup>
                  <RadioLabel>
                    <Radio
                      type="radio"
                      name="organizationType"
                      value="Non-profit"
                      checked={formData.organizationType === 'Non-profit'}
                      onChange={handleInputChange}
                    />
                    Non-profit
                  </RadioLabel>
                  <RadioLabel>
                    <Radio
                      type="radio"
                      name="organizationType"
                      value="For-profit"
                      checked={formData.organizationType === 'For-profit'}
                      onChange={handleInputChange}
                    />
                    For-profit
                  </RadioLabel>
                  <RadioLabel>
                    <Radio
                      type="radio"
                      name="organizationType"
                      value="Private"
                      checked={formData.organizationType === 'Private'}
                      onChange={handleInputChange}
                    />
                    Private
                  </RadioLabel>
                </RadioGroup>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="organizationName">Organization Name</Label>
                <Input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="mailingAddress">Mailing Address</Label>
                <Input
                  type="text"
                  id="mailingAddress"
                  name="mailingAddress"
                  value={formData.mailingAddress}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="cityStateZip">City / State / Zip</Label>
                <Input
                  type="text"
                  id="cityStateZip"
                  name="cityStateZip"
                  value={formData.cityStateZip}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Section>

            <SubmitSection>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </SubmitButton>
              <SubmitNote>
                Once your event is reviewed, a member of our team will reach out.
              </SubmitNote>
            </SubmitSection>
          </Form>
        )}

        <Footer>
          <FooterText>
            Questions? Contact Cristine Minneman at{' '}
            <FooterLink href="mailto:minnemanc@gracehillomaha.com">
              minnemanc@gracehillomaha.com
            </FooterLink>
          </FooterText>
        </Footer>
      </FormContainer>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%);
  padding: 2rem 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #1a365d 0%, #2c5282 100%);
  color: white;
  padding: 2rem;
  text-align: center;
`;

const LogoSection = styled.div`
  margin-bottom: 1rem;
`;

const ChurchName = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 2px;
  margin: 0;
  text-transform: uppercase;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  opacity: 0.9;
  margin: 0;
`;

const Form = styled.form`
  padding: 2rem;
`;

const Section = styled.section`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e2e8f0;
  
  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a365d;
  margin: 0 0 1rem 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #2c5282;
    box-shadow: 0 0 0 3px rgba(44, 82, 130, 0.1);
  }
  
  &::placeholder {
    color: #a0aec0;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #2c5282;
    box-shadow: 0 0 0 3px rgba(44, 82, 130, 0.1);
  }
`;

const CheckboxNote = styled.p`
  font-size: 0.9rem;
  color: #718096;
  margin: 0 0 1rem 0;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.75rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f7fafc;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-right: 0.75rem;
  cursor: pointer;
  accent-color: #2c5282;
`;

const CheckboxText = styled.span`
  font-size: 0.95rem;
  color: #2d3748;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.95rem;
  color: #2d3748;
`;

const Radio = styled.input`
  width: 18px;
  height: 18px;
  margin-right: 0.5rem;
  cursor: pointer;
  accent-color: #2c5282;
`;

const SubmitSection = styled.div`
  text-align: center;
  padding-top: 1rem;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #2c5282 0%, #1a365d 100%);
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 1rem 3rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(26, 54, 93, 0.4);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SubmitNote = styled.p`
  font-size: 0.9rem;
  color: #718096;
  margin-top: 1rem;
`;

const SuccessMessage = styled.div`
  background: #c6f6d5;
  border: 1px solid #9ae6b4;
  border-radius: 8px;
  padding: 2rem;
  margin: 2rem;
  text-align: center;
  
  h3 {
    color: #22543d;
    margin: 0 0 0.5rem 0;
  }
  
  p {
    color: #276749;
    margin: 0;
  }
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  padding: 2rem;
  margin: 2rem;
  text-align: center;
  
  h3 {
    color: #742a2a;
    margin: 0 0 0.5rem 0;
  }
  
  p {
    color: #c53030;
    margin: 0;
  }
`;

const Footer = styled.footer`
  background: #f7fafc;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e2e8f0;
`;

const FooterText = styled.p`
  text-align: center;
  color: #4a5568;
  margin: 0;
  font-size: 0.95rem;
`;

const FooterLink = styled.a`
  color: #2c5282;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

export default FacilityUseForm;
